import type { TourState } from 'types';
import type { Tour, TourStep, TourUser } from './tour-types';
import { can } from 'definitions/capabilities';

/**
 * Which tour to show, and which of its steps. Pure, so every rule in the design can be
 * tested without a DOM. What it cannot decide (whether an element is on screen) is left
 * to useTourTrigger.
 */

const stepVersion = (step: TourStep) => step.since ?? 1;

const shownTo = (step: TourStep, user: TourUser) => !step.when || step.when(user);

type VersionForParams = { tour: Tour; user: TourUser };

/**
 * The version of a tour this user is held to: the tour's own, unless steps hidden from
 * them by `when` are what raised it, in which case the highest version among the steps they
 * can see. Without this, someone who saw version 1 would be offered version 2, find nothing
 * in it and have version 2 recorded, so the step would never reach them once it opened up.
 * `requires` does not lower it: a capability is a role, and that rule predates this one.
 */
export function versionFor({ tour, user }: VersionForParams): number {
  const current = tour.steps.filter((step) => stepVersion(step) <= tour.version);
  if (current.every((step) => shownTo(step, user))) return tour.version;
  return Math.max(1, ...current.filter((step) => shownTo(step, user)).map(stepVersion));
}

/**
 * `/experiments/:id` matches `/experiments/42`; anything else must match exactly.
 *
 * A param matches digits only. Every id in a dashboard URL is numeric, and otherwise
 * `/experiments/:id` would also claim any static `/experiments/...` page without a tour
 * of its own, then wait on every visit for anchors that page never renders.
 */
export function matchesRoute(pathname: string, route: string): boolean {
  const routeParts = route.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);
  if (routeParts.length !== pathParts.length) return false;
  return routeParts.every((part, index) =>
    part.startsWith(':') ? /^\d+$/.test(pathParts[index] ?? '') : part === pathParts[index],
  );
}

/** How specific a route is: its count of fixed segments. `/experiments/library` beats `/experiments/:id`. */
const routeWeight = (route: string) => route.split('/').filter((part) => part && !part.startsWith(':')).length;

type TourForRouteParams = {
  tours: readonly Tour[];
  pathname: string;
  /** The tab the screen reports through `useTourScreen`, if it has tabs. */
  screen?: string | null;
};

/**
 * The tour for this route. The most specific route wins, so a static path is never taken
 * by a `:param` one, and on the same route a tour for the open tab beats the screen's own.
 */
export function tourForRoute({ tours, pathname, screen = null }: TourForRouteParams): Tour | null {
  let best: Tour | null = null;
  let bestWeight = -1;
  for (const tour of tours) {
    if (tour.screen && tour.screen !== screen) continue;
    const matching = (tour.routes ?? []).filter((route) => matchesRoute(pathname, route));
    if (!matching.length) continue;
    const weight = Math.max(...matching.map(routeWeight)) * 2 + (tour.screen ? 1 : 0);
    if (weight > bestWeight) {
      best = tour;
      bestWeight = weight;
    }
  }
  return best;
}

export type PickedTour = {
  tour: Tour;
  seenVersion: number;
  /** What this user is held to and what is recorded when they finish (versionFor). */
  version: number;
};

type PickTourParams = TourForRouteParams & { state: TourState; user?: TourUser };

/**
 * The tour that should start on this route, if any.
 *
 * The platform tour comes first, and holds back every feature tour until it is finished
 * or skipped. A new user gets all of it. An existing user, who never got it, counts as
 * having seen version 1: they know the platform as it was when tours launched, so when
 * the tour gains a step (a new section in the sidebar) they get only that step, marked
 * New, like anyone else who saw it before.
 *
 * A feature tour starts when the user has never seen it, or has seen an older version.
 */
export function pickTour({ tours, pathname, screen, state, user }: PickTourParams): PickedTour | null {
  const platform = tours.find((tour) => tour.key === 'platform');
  if (platform) {
    const seenVersion = state.seen.platform?.version ?? (state.platformTourEligible ? 0 : 1);
    const version = versionFor({ tour: platform, user });
    if (seenVersion < version) return { tour: platform, seenVersion, version };
  }

  const tour = tourForRoute({ tours, pathname, screen });
  if (!tour) return null;

  const seenVersion = state.seen[tour.key]?.version ?? 0;
  const version = versionFor({ tour, user });
  return seenVersion < version ? { tour, seenVersion, version } : null;
}

type StepsForParams = {
  tour: Tour;
  /** 0 when never seen. */
  seenVersion: number;
  user: TourUser;
  /** A replay from Help shows every current step, whatever was seen before. */
  isReplay?: boolean;
};

/**
 * The steps this user may see, before checking what is on screen.
 *
 * An update shows only what was added since the version they saw. Steps that need a
 * capability the user lacks, or whose `when` is false for them, are dropped, so nobody is
 * walked to a button they cannot use.
 */
export function stepsFor({ tour, seenVersion, user, isReplay = false }: StepsForParams): TourStep[] {
  const current = tour.steps.filter((step) => stepVersion(step) <= tour.version);
  const fresh = !isReplay && seenVersion > 0 ? current.filter((step) => stepVersion(step) > seenVersion) : current;
  return fresh.filter((step) => (!step.requires || can(user, step.requires)) && shownTo(step, user));
}
